import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { StockAdjustment } from "@artisancode/api-types";

interface Props {
  adjustment: StockAdjustment | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
}

export function StockAdjustmentCancelDialog({
  adjustment,
  onOpenChange,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleConfirm() {
    if (!reason.trim()) return;
    try {
      setIsSubmitting(true);
      await onConfirm(reason.trim());
      setReason("");
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={adjustment !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <span>Batalkan Penyesuaian Stok?</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Penyesuaian{" "}
            <strong className="text-foreground">{adjustment?.code}</strong>{" "}
            untuk{" "}
            <strong className="text-foreground">{adjustment?.itemName}</strong>{" "}
            akan dibatalkan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="rounded-md border border-amber-500/40 bg-amber-50/50 dark:bg-amber-950/20 p-2.5 text-xs text-amber-800 dark:text-amber-200">
            <span className="font-semibold">Perhatian:</span> Pembatalan akan
            membuat mutasi pembalik otomatis pada kartu stok sehingga saldo
            barang akan kembali ke kondisi semula.
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cancel-adj-reason" className="text-xs">
              Alasan Pembatalan <span className="text-destructive">*</span>:
            </Label>
            <Input
              id="cancel-adj-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: Salah pilih item, input dibatalkan..."
              className="text-xs"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-h-[40px] w-full sm:w-auto text-xs"
          >
            Tutup
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={isSubmitting || !reason.trim()}
            className="min-h-[40px] w-full sm:w-auto text-xs"
          >
            {isSubmitting ? "Membatalkan..." : "Ya, Batalkan Penyesuaian"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
